import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  LinearProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  OutlinedInput,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';

import { SalaryProfileService, EmployeeService } from '../../services/payrollService';
import { SalaryProfile, Employee, ContractType } from '../../types/payroll';
import { formatCurrency } from '../../utils/formatters';
import { useRole } from '../../hooks/useRole';
import ConfirmDialog from '../common/ConfirmDialog';

const SalaryProfileForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasAnyRole } = useRole();
  
  const [salaryProfile, setSalaryProfile] = useState<SalaryProfile | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [formData, setFormData] = useState<Partial<SalaryProfile>>({
    employee_id: 0,
    base_salary_vnd: 0,
    contract_type: 'full-time' as ContractType,
    is_active: true,
    standard_work_days: 0,
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: null,
    si_rate_employee: 8,
    hi_rate_employee: 1.5,
    ui_rate_employee: 1,
    si_rate_company: 17,
    hi_rate_company: 3,
    ui_rate_company: 1,
    si_contribution_base_vnd: 0,
    hi_contribution_base_vnd: 0,
    ui_contribution_base_vnd: 0,
    personal_deduction_vnd: 0,
    dependent_deduction_vnd: 0,
    dependent_count: 0,
    notes: ''
  });
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  
  // Ensure user has permission
  const canManageSalaries = hasAnyRole(['hr', 'admin']);
  const isEditMode = !!id;
  
  // Load data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch employees for dropdown
        const employeesResponse = await EmployeeService.getAllEmployees();
        setEmployees(employeesResponse.data);
        
        // If editing, fetch salary profile
        if (isEditMode) {
          const profileResponse = await SalaryProfileService.getSalaryProfileById(Number(id));
          const profile = profileResponse.data;
          
          setSalaryProfile(profile);
          setFormData({
            employee_id: profile.employee_id,
            base_salary_vnd: profile.base_salary_vnd,
            contract_type: profile.contract_type,
            standard_work_days: profile.standard_work_days,
            effective_from: profile.effective_from,
            effective_to: profile.effective_to || null,
            si_rate_employee: profile.si_rate_employee,
            hi_rate_employee: profile.hi_rate_employee,
            ui_rate_employee: profile.ui_rate_employee,
            si_rate_company: profile.si_rate_company,
            hi_rate_company: profile.hi_rate_company,
            ui_rate_company: profile.ui_rate_company,
            si_contribution_base_vnd: profile.si_contribution_base_vnd,
            hi_contribution_base_vnd: profile.hi_contribution_base_vnd,
            ui_contribution_base_vnd: profile.ui_contribution_base_vnd,
            personal_deduction_vnd: profile.personal_deduction_vnd,
            dependent_deduction_vnd: profile.dependent_deduction_vnd,
            dependent_count: profile.dependent_count,
            notes: profile.notes
          });
        }
        
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load data');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, isEditMode]);
  
  // Handle field changes
  const handleChange = (field: keyof SalaryProfile, value: any) => {
    setFormData({
      ...formData,
      [field]: value
    });
    
    // Clear error for this field if exists
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: ''
      });
    }
    
    // Clear any success messages when form changes
    if (successMessage) {
      setSuccessMessage(null);
    }
  };
  
  // Handle number input changes
  const handleNumberChange = (field: keyof SalaryProfile, value: string) => {
    const numValue = value === '' ? undefined : Number(value);
    handleChange(field, numValue);
  };
  
  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.employee_id) {
      errors.employee_id = 'Employee is required';
    }
    
    if (!formData.base_salary_vnd) {
      errors.base_salary_vnd = 'Base salary is required';
    } else if (formData.base_salary_vnd < 0) {
      errors.base_salary_vnd = 'Base salary cannot be negative';
    }
    
    if (!formData.contract_type) {
      errors.contract_type = 'Contract type is required';
    }
    
    if (!formData.standard_work_days) {
      errors.standard_work_days = 'Standard work days is required';
    } else if (formData.standard_work_days <= 0 || formData.standard_work_days > 31) {
      errors.standard_work_days = 'Standard work days must be between 1 and 31';
    }
    
    if (!formData.effective_from) {
      errors.effective_from = 'Effective from date is required';
    }
    
    if (formData.effective_from && formData.effective_to && 
        new Date(formData.effective_from) >= new Date(formData.effective_to)) {
      errors.effective_to = 'Must be after Effective from date';
    }
    
    // Validate insurance rates
    ['si_rate_employee', 'hi_rate_employee', 'ui_rate_employee', 
     'si_rate_company', 'hi_rate_company', 'ui_rate_company'].forEach(field => {
      const key = field as keyof SalaryProfile;
      if (formData[key] === undefined || formData[key] === null) {
        errors[field] = 'Rate is required';
      } else if (Number(formData[key]) < 0 || Number(formData[key]) > 100) {
        errors[field] = 'Rate must be between 0 and 100';
      }
    });
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle save
  const handleSave = async () => {
    if (!canManageSalaries) return;
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      if (isEditMode) {
        // Update existing profile
        await SalaryProfileService.updateSalaryProfile(Number(id), formData);
      } else {
        // Create new profile
        await SalaryProfileService.createSalaryProfile(formData);
      }
      
      setSuccessMessage(`Salary profile ${isEditMode ? 'updated' : 'created'} successfully`);
      
      // Navigate away after successful creation
      if (!isEditMode) {
        setTimeout(() => {
          navigate('/payroll/employees');
        }, 1500);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} salary profile`);
      console.error('Error saving salary profile:', err);
      setSuccessMessage(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle back
  const handleBack = () => {
    // If form has changes, confirm before navigating away
    if (isFormChanged()) {
      setConfirmDialogOpen(true);
    } else {
      navigateBack();
    }
  };
  
  // Check if form has been modified
  const isFormChanged = (): boolean => {
    if (!isEditMode) {
      // For new profiles, check if any required fields have been filled in
      return !!(
        formData.employee_id || 
        formData.base_salary_vnd || 
        formData.notes
      );
    }
    
    // For existing profiles, compare with original data
    if (!salaryProfile) return false;
    
    return (
      formData.base_salary_vnd !== salaryProfile.base_salary_vnd ||
      formData.contract_type !== salaryProfile.contract_type ||
      formData.standard_work_days !== salaryProfile.standard_work_days ||
      formData.effective_from !== salaryProfile.effective_from ||
      formData.effective_to !== salaryProfile.effective_to ||
      formData.si_rate_employee !== salaryProfile.si_rate_employee ||
      formData.hi_rate_employee !== salaryProfile.hi_rate_employee ||
      formData.ui_rate_employee !== salaryProfile.ui_rate_employee ||
      formData.si_rate_company !== salaryProfile.si_rate_company ||
      formData.hi_rate_company !== salaryProfile.hi_rate_company ||
      formData.ui_rate_company !== salaryProfile.ui_rate_company ||
      formData.si_contribution_base_vnd !== salaryProfile.si_contribution_base_vnd ||
      formData.hi_contribution_base_vnd !== salaryProfile.hi_contribution_base_vnd ||
      formData.ui_contribution_base_vnd !== salaryProfile.ui_contribution_base_vnd ||
      formData.personal_deduction_vnd !== salaryProfile.personal_deduction_vnd ||
      formData.dependent_deduction_vnd !== salaryProfile.dependent_deduction_vnd ||
      formData.dependent_count !== salaryProfile.dependent_count ||
      formData.notes !== salaryProfile.notes
    );
  };
  
  // Navigate back to previous page
  const navigateBack = () => {
    if (isEditMode) {
      navigate(`/payroll/employees/${salaryProfile?.employee_id}`);
    } else {
      navigate('/payroll/employees');
    }
  };
  
  // Handle confirm dialog
  const handleConfirmDiscard = () => {
    setConfirmDialogOpen(false);
    navigateBack();
  };
  
  const handleCancelDiscard = () => {
    setConfirmDialogOpen(false);
  };
  
  if (!canManageSalaries) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            You do not have permission to manage salary profiles.
          </Typography>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/payroll/employees')} 
            sx={{ mt: 2 }}
          >
            Back to Employees
          </Button>
        </Paper>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center">
            <IconButton onClick={handleBack} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" component="h1">
              {isEditMode ? 'Edit Salary Profile' : 'Create New Salary Profile'}
            </Typography>
          </Box>
          
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            Save Profile
          </Button>
        </Box>
        
        {loading && <LinearProgress sx={{ mb: 3 }} />}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}
        
        <Box component="form">
          {/* Basic Information Section */}
          <Box mb={4}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 }}>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <FormControl 
                  fullWidth
                  error={!!formErrors.employee_id}
                  disabled={isEditMode} // Cannot change employee in edit mode
                >
                  <InputLabel id="employee-select-label">Employee</InputLabel>
                  <Select
                    labelId="employee-select-label"
                    value={formData.employee_id || ''}
                    onChange={(e) => handleChange('employee_id', e.target.value)}
                    label="Employee"
                  >
                    <MenuItem value="">
                      <em>Select an employee</em>
                    </MenuItem>
                    {employees.map((employee) => (
                      <MenuItem key={employee.id} value={employee.id}>
                        {employee.employee_no} - {employee.first_name_en} {employee.last_name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.employee_id && (
                    <FormHelperText>{formErrors.employee_id}</FormHelperText>
                  )}
                </FormControl>
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <FormControl 
                  fullWidth 
                  variant="outlined"
                  error={!!formErrors.base_salary_vnd}
                >
                  <InputLabel>Base Salary</InputLabel>
                  <OutlinedInput
                    value={formData.base_salary_vnd ?? ''}
                    onChange={(e) => handleNumberChange('base_salary_vnd', e.target.value)}
                    startAdornment={<InputAdornment position="start">₫</InputAdornment>}
                    label="Base Salary"
                    type="number"
                  />
                  {formErrors.base_salary_vnd && (
                    <FormHelperText>{formErrors.base_salary_vnd}</FormHelperText>
                  )}
                </FormControl>
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <FormControl 
                  fullWidth
                  error={!!formErrors.contract_type}
                >
                  <InputLabel id="contract-type-label">Contract Type</InputLabel>
                  <Select
                    labelId="contract-type-label"
                    value={formData.contract_type || 'FULL_TIME'}
                    onChange={(e) => handleChange('contract_type', e.target.value)}
                    label="Contract Type"
                  >
                    <MenuItem value="FULL_TIME">Full Time</MenuItem>
                    <MenuItem value="PART_TIME">Part Time</MenuItem>
                    <MenuItem value="CONTRACTOR">Contractor</MenuItem>
                    <MenuItem value="PROBATION">Probation</MenuItem>
                    <MenuItem value="INTERN">Intern</MenuItem>
                  </Select>
                  {formErrors.contract_type && (
                    <FormHelperText>{formErrors.contract_type}</FormHelperText>
                  )}
                </FormControl>
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <TextField
                  label="Standard Working Days per Month"
                  type="number"
                  value={formData.standard_work_days ?? ''}
                  onChange={(e) => handleNumberChange('standard_work_days', e.target.value)}
                  fullWidth
                  error={!!formErrors.standard_work_days}
                  helperText={formErrors.standard_work_days}
                  InputProps={{
                    inputProps: { min: 1, max: 31, step: 0.5 }
                  }}
                />
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Effective From"
                    value={formData.effective_from ? new Date(formData.effective_from) : null}
                    onChange={(date) => handleChange('effective_from', date?.toISOString().split('T')[0] || null)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!formErrors.effective_from,
                        helperText: formErrors.effective_from
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Effective To (Optional)"
                    value={formData.effective_to ? new Date(formData.effective_to) : null}
                    onChange={(date) => handleChange('effective_to', date?.toISOString().split('T')[0] || null)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!formErrors.effective_to,
                        helperText: formErrors.effective_to
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </Box>
          
          {/* Insurance Section */}
          <Box mb={4}>
            <Box display="flex" alignItems="center">
              <Typography variant="h6" gutterBottom>
                Insurance & Contributions
              </Typography>
              <Tooltip title="Insurance rates are percentages of the contribution base. Standard rates: SI (8% employee / 17% company), HI (1.5% employee / 3% company), UI (1% employee / 1% company)">
                <IconButton size="small" sx={{ ml: 1, mb: 1 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 }}>
              {/* Social Insurance */}
              <Box sx={{ gridColumn: 'span 12' }}>
                <Typography variant="subtitle1">Social Insurance</Typography>
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                <TextField
                  label="SI Employee Rate (%)"
                  type="number"
                  value={formData.si_rate_employee ?? ''}
                  onChange={(e) => handleNumberChange('si_rate_employee', e.target.value)}
                  fullWidth
                  error={!!formErrors.si_rate_employee}
                  helperText={formErrors.si_rate_employee}
                  InputProps={{
                    inputProps: { min: 0, max: 100, step: 0.1 }
                  }}
                />
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                <TextField
                  label="SI Company Rate (%)"
                  type="number"
                  value={formData.si_rate_company ?? ''}
                  onChange={(e) => handleNumberChange('si_rate_company', e.target.value)}
                  fullWidth
                  error={!!formErrors.si_rate_company}
                  helperText={formErrors.si_rate_company}
                  InputProps={{
                    inputProps: { min: 0, max: 100, step: 0.1 }
                  }}
                />
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>SI Contribution Base</InputLabel>
                  <OutlinedInput
                    value={formData.si_contribution_base_vnd ?? ''}
                    onChange={(e) => handleNumberChange('si_contribution_base_vnd', e.target.value)}
                    startAdornment={<InputAdornment position="start">₫</InputAdornment>}
                    label="SI Contribution Base"
                    type="number"
                    placeholder="Default: Base Salary"
                  />
                  <FormHelperText>Leave blank to use base salary</FormHelperText>
                </FormControl>
              </Box>
              
              {/* Health Insurance */}
              <Box sx={{ gridColumn: 'span 12' }}>
                <Typography variant="subtitle1">Health Insurance</Typography>
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                <TextField
                  label="HI Employee Rate (%)"
                  type="number"
                  value={formData.hi_rate_employee ?? ''}
                  onChange={(e) => handleNumberChange('hi_rate_employee', e.target.value)}
                  fullWidth
                  error={!!formErrors.hi_rate_employee}
                  helperText={formErrors.hi_rate_employee}
                  InputProps={{
                    inputProps: { min: 0, max: 100, step: 0.1 }
                  }}
                />
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                <TextField
                  label="HI Company Rate (%)"
                  type="number"
                  value={formData.hi_rate_company ?? ''}
                  onChange={(e) => handleNumberChange('hi_rate_company', e.target.value)}
                  fullWidth
                  error={!!formErrors.hi_rate_company}
                  helperText={formErrors.hi_rate_company}
                  InputProps={{
                    inputProps: { min: 0, max: 100, step: 0.1 }
                  }}
                />
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>HI Contribution Base</InputLabel>
                  <OutlinedInput
                    value={formData.hi_contribution_base_vnd ?? ''}
                    onChange={(e) => handleNumberChange('hi_contribution_base_vnd', e.target.value)}
                    startAdornment={<InputAdornment position="start">₫</InputAdornment>}
                    label="HI Contribution Base"
                    type="number"
                    placeholder="Default: Base Salary"
                  />
                  <FormHelperText>Leave blank to use base salary</FormHelperText>
                </FormControl>
              </Box>
              
              {/* Unemployment Insurance */}
              <Box sx={{ gridColumn: 'span 12' }}>
                <Typography variant="subtitle1">Unemployment Insurance</Typography>
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                <TextField
                  label="UI Employee Rate (%)"
                  type="number"
                  value={formData.ui_rate_employee ?? ''}
                  onChange={(e) => handleNumberChange('ui_rate_employee', e.target.value)}
                  fullWidth
                  error={!!formErrors.ui_rate_employee}
                  helperText={formErrors.ui_rate_employee}
                  InputProps={{
                    inputProps: { min: 0, max: 100, step: 0.1 }
                  }}
                />
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                <TextField
                  label="UI Company Rate (%)"
                  type="number"
                  value={formData.ui_rate_company ?? ''}
                  onChange={(e) => handleNumberChange('ui_rate_company', e.target.value)}
                  fullWidth
                  error={!!formErrors.ui_rate_company}
                  helperText={formErrors.ui_rate_company}
                  InputProps={{
                    inputProps: { min: 0, max: 100, step: 0.1 }
                  }}
                />
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>UI Contribution Base</InputLabel>
                  <OutlinedInput
                    value={formData.ui_contribution_base_vnd ?? ''}
                    onChange={(e) => handleNumberChange('ui_contribution_base_vnd', e.target.value)}
                    startAdornment={<InputAdornment position="start">₫</InputAdornment>}
                    label="UI Contribution Base"
                    type="number"
                    placeholder="Default: Base Salary"
                  />
                  <FormHelperText>Leave blank to use base salary</FormHelperText>
                </FormControl>
              </Box>
            </Box>
          </Box>
          
          {/* Tax Deductions Section */}
          <Box mb={4}>
            <Typography variant="h6" gutterBottom>
              Tax Deductions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 }}>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Personal Deduction</InputLabel>
                  <OutlinedInput
                    value={formData.personal_deduction_vnd ?? ''}
                    onChange={(e) => handleNumberChange('personal_deduction_vnd', e.target.value)}
                    startAdornment={<InputAdornment position="start">₫</InputAdornment>}
                    label="Personal Deduction"
                    type="number"
                    placeholder="Default: 11,000,000"
                  />
                  <FormHelperText>Standard: 11,000,000 VND</FormHelperText>
                </FormControl>
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Dependent Deduction per Person</InputLabel>
                  <OutlinedInput
                    value={formData.dependent_deduction_vnd ?? ''}
                    onChange={(e) => handleNumberChange('dependent_deduction_vnd', e.target.value)}
                    startAdornment={<InputAdornment position="start">₫</InputAdornment>}
                    label="Dependent Deduction per Person"
                    type="number"
                    placeholder="Default: 4,400,000"
                  />
                  <FormHelperText>Standard: 4,400,000 VND</FormHelperText>
                </FormControl>
              </Box>
              
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                <TextField
                  label="Number of Dependents"
                  type="number"
                  value={formData.dependent_count ?? ''}
                  onChange={(e) => handleNumberChange('dependent_count', e.target.value)}
                  fullWidth
                  InputProps={{
                    inputProps: { min: 0, step: 1 }
                  }}
                />
              </Box>
            </Box>
          </Box>
          
          {/* Notes Section */}
          <Box mb={4}>
            <Typography variant="h6" gutterBottom>
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 }}>
              <Box sx={{ gridColumn: 'span 12' }}>
                <TextField
                  label="Notes"
                  value={formData.notes || ''}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Box>
            </Box>
          </Box>
          
          <Box display="flex" justifyContent="flex-end" mt={4}>
            <Button 
              variant="outlined" 
              onClick={handleBack} 
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSave}
              disabled={loading}
              startIcon={<SaveIcon />}
            >
              {isEditMode ? 'Update' : 'Create'} Salary Profile
            </Button>
          </Box>
        </Box>
      </Paper>
      
      {/* Confirmation Dialog for Unsaved Changes */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Discard Changes"
        content="You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost."
        onConfirm={handleConfirmDiscard}
        onCancel={handleCancelDiscard}
        confirmText="Discard Changes"
        confirmColor="error"
      />
    </Container>
  );
};

export default SalaryProfileForm;
